import { fr } from 'date-fns/locale';
import { fromIsoDay, isoDay } from 'helper/timeLabels';
import { useEffect, useRef } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import useInlineField from './useInlineField';

registerLocale('fr-FR', fr);

/**
 * The due day of a task, in its panel's summary: a click opens the form's
 * calendar under it, a day picked is saved at once; Escape or a click
 * elsewhere closes it unchanged. `renderCell(dueOn)` draws the summary cell
 * of a day (the new one while it is being saved). `canEdit` false: read only.
 */
const TaskDueField = ({ dueOn, canEdit, onSave, renderCell, className = '' }) => {
  const field = useInlineField({ value: dueOn, onSave: (next) => onSave({ dueOn: next }) });
  const box = useRef(null);
  const { editing, cancel } = field;
  useEffect(() => {
    if (!editing) return undefined;
    const closeOutside = (event) => box.current && !box.current.contains(event.target) && cancel();
    document.addEventListener('mousedown', closeOutside);
    return () => document.removeEventListener('mousedown', closeOutside);
  }, [editing, cancel]);

  if (!canEdit) return <div className={className}>{renderCell(dueOn)}</div>;
  return (
    <div ref={box} className={`relative ${className}`}>
      <button
        type="button"
        aria-expanded={editing}
        onClick={() => (editing ? cancel() : field.start())}
        className="block w-full text-left transition-colors hover:bg-gray-50"
      >
        {renderCell(field.shown)}
      </button>
      {editing && (
        <div className="absolute left-0 top-full z-30 mt-1">
          <DatePicker
            inline
            locale="fr-FR"
            selected={fromIsoDay(field.shown)}
            onChange={(date) => date && field.commit(isoDay(date))}
          />
        </div>
      )}
    </div>
  );
};

export default TaskDueField;
